import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import {
  pushPermissionStatus,
  registerPushIfGranted,
  requestPushPermissionAndRegister,
  type PushPermission
} from '@/lib/push';
import { canOpenAppSettings, openAppSettings } from '@/lib/location';

/**
 * Linha "Ativar notificações" do Perfil (2026-10-22).
 *
 * Quem tocou "Agora não" no onboarding ou negou o pedido do sistema não tinha
 * mais como ligar o push por dentro do app — o card "Permissões" do Perfil saiu
 * em 2026-10-08 — e metade dos clientes ativos estava sem push registrado. Sem
 * push, a recarga iniciada pelo suporte (recarga remota do admin) e a recarga
 * concluída não avisam ninguém.
 *
 * Só aparece no app nativo e só enquanto a permissão não está dada (uma linha
 * na lista de atalhos, pra caber no Perfil sem rolagem):
 *  - nunca pedida → dispara o pedido do sistema (no iOS ele só existe uma vez,
 *    e o onboarding nunca o queima no "Agora não");
 *  - negada (ou o pedido voltou negado) → abre os Ajustes do app; na volta
 *    (`visibilitychange`) relê a permissão e registra o token.
 * Toda chamada ao plugin tem teto: plugin que nunca liquida não pode congelar
 * a linha (CLAUDE.md §10, falha de plugin nativo vira estado visível).
 */

const CHECK_TIMEOUT_MS = 8000;
// O pedido do sistema espera o cliente responder; o teto é só pra não
// prender o botão se o plugin nunca devolver.
const REQUEST_TIMEOUT_MS = 60000;

export type NotificationsRowState = PushPermission | 'enabled-now' | null;

export interface NotificationsRowCopy {
  label: string;
  sub: string | null;
  value: string | null;
  action: 'request' | 'settings' | null;
}

/** O que a linha mostra em cada estado. `null` = linha escondida. */
export function notificationsRowCopy(
  state: NotificationsRowState,
  platform: 'ios' | 'android',
  canOpenSettings: boolean
): NotificationsRowCopy | null {
  if (state === null || state === 'granted') return null;
  if (state === 'enabled-now') {
    return { label: 'Notificações', sub: null, value: 'Ativadas', action: null };
  }
  if (state === 'prompt') {
    return {
      label: 'Ativar notificações',
      sub: 'Saiba quando a recarga terminar',
      value: null,
      action: 'request'
    };
  }
  if (canOpenSettings) {
    return {
      label: 'Ativar notificações',
      sub: 'Ligue em Notificações nos ajustes',
      value: null,
      action: 'settings'
    };
  }
  return {
    label: 'Ativar notificações',
    sub:
      platform === 'ios'
        ? 'Em Ajustes › Bow › Notificações'
        : 'Em Configurações › Apps › Bow › Notificações',
    value: null,
    action: null
  };
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('plugin-timeout')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export function useNotificationsRow(): {
  copy: NotificationsRowCopy | null;
  busy: boolean;
  activate: () => void;
} {
  const native = Capacitor.isNativePlatform();
  const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  const [state, setState] = useState<NotificationsRowState>(null);
  const [busy, setBusy] = useState(false);
  const [settingsFailed, setSettingsFailed] = useState(false);

  const refresh = useCallback(async () => {
    let perm: PushPermission;
    try {
      perm = await withTimeout(pushPermissionStatus(), CHECK_TIMEOUT_MS);
    } catch {
      perm = 'denied';
    }
    if (perm !== 'granted') {
      // Pedido já recusado nesta tela: fica em "denied" mesmo que o plugin
      // ainda diga "prompt" (Android depois da 2ª recusa não mostra mais nada).
      setState((prev) => (prev === 'denied' && perm === 'prompt' ? 'denied' : perm));
      return;
    }
    // Liberou (aqui ou nos Ajustes): registra o token na hora, sem esperar o
    // próximo boot. Idempotente por sessão.
    await registerPushIfGranted();
    // 1ª leitura já concedida → linha escondida; virou concedida depois de a
    // linha aparecer → confirma com "Ativadas".
    setState((prev) => (prev === null || prev === 'granted' ? 'granted' : 'enabled-now'));
  }, []);

  useEffect(() => {
    if (!native) return;
    void refresh();
    // Volta dos Ajustes do sistema: o WebView fica hidden em background.
    const onWake = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onWake);
    return () => document.removeEventListener('visibilitychange', onWake);
  }, [native, refresh]);

  const copy = native
    ? notificationsRowCopy(state, platform, canOpenAppSettings() && !settingsFailed)
    : null;

  const activate = useCallback(() => {
    if (busy || !copy?.action) return;
    const action = copy.action;
    setBusy(true);
    void (async () => {
      try {
        if (action === 'request') {
          const result = await withTimeout(
            requestPushPermissionAndRegister(),
            REQUEST_TIMEOUT_MS
          ).catch(() => 'denied' as const);
          setState(result === 'granted' ? 'enabled-now' : 'denied');
        } else if (!(await openAppSettings())) {
          setSettingsFailed(true);
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [busy, copy?.action]);

  return { copy, busy, activate };
}
